/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import React from 'react';
import { Tooltip, Spinner as Loader } from 'react-bootstrap';
import {
  Check,
  CircleMinus,
  CirclePlus,
  Cross,
  InfoCircle,
  Lock,
  Minus,
  Plus,
} from '@saleshandy/icons';
import { SkeletonLoading } from '@saleshandy/design-system';
import classNames from 'classnames';
import InfiniteScroll from 'react-infinite-scroll-component';
import Checkbox from '../../../../../shared/design-system/components/atoms/checkbox';
import {
  OverlayTooltip,
  Placement,
} from '../../../../../shared/design-system/components/overlay';
import TruncatedTextWithAutoTooltip from '../../../../../shared/components/truncated-text-with-auto-tooltip';
import {
  SuggestionItem,
  getSuggestionValue,
  getSuggestionKey,
  isV2Suggestion,
  isCompanySuggestion,
  resolveFlagImageIso2,
} from './v2-filter-checkbox.helpers';
import { accessibleOnClick } from '../../../../../shared/utils';
import { getCountryFlagImageUrl } from '../../../../../shared/components/country-code-phone-input-picker/country-flag-image';

interface RenderAutocompleteProps {
  suggestions: SuggestionItem[];
  onSelectOption: (option: string) => void;
  filterKey: string;
  selectedSuggestionIndex: number;
  handleOnChange: (params: {
    action: string;
    groupName: string;
    childName?: string;
    exclude?: boolean;
    skipImmediateApplyOnRemove?: boolean;
  }) => void;
  setShowAutoComplete: (value: boolean) => void;
  isIncludeDisabled: boolean;
  isExcludeDisabled?: boolean;
  includeTooltip: string;
  excludeTooltip?: string;
  isV2Response: boolean;
  isLoading: boolean;
  hideIncludeExclude?: boolean;
  inputLabel?: string;
  hasMore?: boolean;
  onLoadMore?: () => void;
  // Location v2 interception: when set and returns true, the default pick
  // action is skipped (parent routes the suggestion to the v2 URL param).
  onV2LocationPick?: (
    suggestion: SuggestionItem,
    isExclude: boolean,
  ) => boolean;
  onV2LocationReset?: (
    suggestion: SuggestionItem,
    isExclude: boolean,
  ) => boolean;
  includedGroupNames?: string[];
  excludedGroupNames?: string[];
}

interface RenderCheckboxGroupProps {
  label: string;
  name: string;
  checked: boolean | 'indeterminate';
  childItems: any[];
  expanded: string[];
  handleOnChange: (params: {
    action: string;
    groupName: string;
    childName?: string;
  }) => void;
  filterKey: string;
  infoCircleToolTip?: string;
  hideIncludeExclude?: boolean;
  isIncludeDisabled: boolean;
  includeTooltip: string;
  isPhoneNumberNudgeOpen: boolean;
  onToggleExpand?: (key: string) => void;
  tooltipText?: string;
  parentLocked?: boolean;
  onLockClick?: () => void;
}

export const getLabelTooltip = (text: string) => (
  <Tooltip id="popover-basic" className="bs-tooltip-inner">
    {text}
  </Tooltip>
);

export const RenderAutocomplete: React.FC<RenderAutocompleteProps> = ({
  inputLabel,
  suggestions,
  onSelectOption,
  filterKey,
  selectedSuggestionIndex,
  handleOnChange,
  setShowAutoComplete,
  isIncludeDisabled,
  isExcludeDisabled = false,
  includeTooltip,
  excludeTooltip = 'Exclude',
  isV2Response = false,
  isLoading = false,
  hideIncludeExclude = false,
  hasMore = false,
  onLoadMore,
  onV2LocationPick,
  onV2LocationReset,
  includedGroupNames = [],
  excludedGroupNames = [],
}) => {
  const skeletonCount = ['location'].includes(filterKey) ? 5 : 3;
  const scrollableId = `autocomplete-scroll-${filterKey}`;
  const isInitialLoading = isLoading && suggestions?.length === 0;
  const isNoResults = suggestions?.length === 0 && !isLoading;

  const renderActionArea = (
    suggestionKey: string,
    groupNameForAction: string,
    suggestion: SuggestionItem,
  ) => {
    const isIncluded = includedGroupNames.includes(groupNameForAction);
    const isExcluded = excludedGroupNames.includes(groupNameForAction);
    const hasAction = isIncluded || isExcluded;

    const resetAppliedOption = (isExclude: boolean) => {
      if (onV2LocationReset?.(suggestion, isExclude)) return;
      handleOnChange({
        action: 'removeTag',
        groupName: groupNameForAction,
        exclude: isExclude,
        skipImmediateApplyOnRemove: true,
      });
    };

    const includeOption = () => {
      if (isIncludeDisabled) return;
      if (onV2LocationPick?.(suggestion, false)) return;
      handleOnChange({
        action: 'inputOption',
        groupName: groupNameForAction,
      });
    };

    const excludeOption = () => {
      if (isExcludeDisabled) return;
      if (onV2LocationPick?.(suggestion, true)) return;
      handleOnChange({
        action: 'excludeOption',
        groupName: groupNameForAction,
      });
    };

    if (hasAction) {
      return (
        <div className="autocomplete-suggestion-actions autocomplete-suggestion-actions--applied">
          <span
            className={`filter-action-chip filter-action-chip--${
              isIncluded ? 'included' : 'excluded'
            }`}
            onMouseDown={(e) => e.preventDefault()}
            onClick={(e) => {
              e.stopPropagation();
              resetAppliedOption(isExcluded);
            }}
          >
            {isIncluded ? 'Included' : 'Excluded'}
          </span>
          <div className="include-exclude-icons">
            {isIncluded ? (
              <>
                <OverlayTooltip
                  text="Reset selection"
                  placement={Placement.Bottom}
                >
                  <div>
                    <Check
                      className="circle-status-active circle-status-included"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={(e) => {
                        e.stopPropagation();
                        resetAppliedOption(false);
                      }}
                    />
                  </div>
                </OverlayTooltip>

                <OverlayTooltip text="Exclude" placement={Placement.Bottom}>
                  <div>
                    <CircleMinus
                      className="circle-minus"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={(e) => {
                        e.stopPropagation();
                        excludeOption();
                      }}
                    />
                  </div>
                </OverlayTooltip>
              </>
            ) : (
              <>
                <OverlayTooltip text="Include" placement={Placement.Bottom}>
                  <div>
                    <CirclePlus
                      className={`circle-plus ${
                        isIncludeDisabled
                          ? 'include-disabled'
                          : 'include-enabled'
                      }`}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={(e) => {
                        e.stopPropagation();
                        includeOption();
                      }}
                    />
                  </div>
                </OverlayTooltip>

                <OverlayTooltip
                  text="Reset selection"
                  placement={Placement.Bottom}
                >
                  <div>
                    <Cross
                      className="circle-status-active circle-status-excluded"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={(e) => {
                        e.stopPropagation();
                        resetAppliedOption(true);
                      }}
                    />
                  </div>
                </OverlayTooltip>
              </>
            )}
          </div>
        </div>
      );
    }

    return (
      <div className="include-exclude-icons">
        <OverlayTooltip
          placement={Placement.Bottom}
          id={`tooltip-${suggestionKey}-include-child-${suggestionKey}`}
          className="bs-tooltip-inner"
          text={includeTooltip}
        >
          <CirclePlus
            className={`circle-plus ${
              isIncludeDisabled ? 'include-disabled' : 'include-enabled'
            }`}
            onMouseDown={(e) => e.preventDefault()}
            onClick={(e) => {
              e.stopPropagation();
              includeOption();
            }}
          />
        </OverlayTooltip>

        <OverlayTooltip
          placement={Placement.Bottom}
          id={`tooltip-${suggestionKey}-exclude-child-${suggestionKey}`}
          className="bs-tooltip-inner"
          text={excludeTooltip}
        >
          <CircleMinus
            className={`circle-minus ${
              isExcludeDisabled ? 'include-disabled' : ''
            }`}
            onMouseDown={(e) => e.preventDefault()}
            onClick={(e) => {
              e.stopPropagation();
              if (isExcludeDisabled) return;
              excludeOption();
            }}
          />
        </OverlayTooltip>
      </div>
    );
  };

  const renderSuggestionItems = () =>
    suggestions?.map((suggestion, index) => {
      const suggestionValue = getSuggestionValue(suggestion);
      const suggestionKey = getSuggestionKey(suggestion, index);
      const isSelected = index === selectedSuggestionIndex;
      const isCompany = isCompanySuggestion(suggestion);

      // For company_domain / website_domain use companyDomain; for company_ids use companyId; for competitors_company_name use companyName
      let groupNameForAction = suggestionValue;
      if (isCompany) {
        if (filterKey === 'competitors_company_name') {
          groupNameForAction = suggestion?.companyName;
        } else if (
          (filterKey === 'company_domain' || filterKey === 'website_domain') &&
          suggestion?.companyDomain
        ) {
          groupNameForAction = suggestion?.companyDomain;
        } else {
          groupNameForAction = String(suggestion?.companyId);
        }
      }

      // Skip rendering company suggestions without domain when filter is company_domain or website_domain
      if (
        (filterKey === 'company_domain' || filterKey === 'website_domain') &&
        isCompany &&
        !suggestion?.companyDomain
      ) {
        return null;
      }

      const v2FlagIso2 =
        isV2Response && isV2Suggestion(suggestion) && suggestion.emoji
          ? resolveFlagImageIso2(suggestion.emoji)
          : null;
      const v2FlagImageSrc = v2FlagIso2
        ? getCountryFlagImageUrl(v2FlagIso2)
        : '';

      const isIncluded = includedGroupNames.includes(groupNameForAction);
      const isExcluded = excludedGroupNames.includes(groupNameForAction);
      const isApplied = isIncluded || isExcluded;
      const shouldShowAppliedState = !hideIncludeExclude && isApplied;

      return (
        <li
          key={suggestionKey}
          role="option"
          aria-selected={isSelected}
          tabIndex={0}
          onMouseDown={(e) => {
            // Prevent input blur and prevent document "outside click" handler from firing.
            e.preventDefault();
            e.stopPropagation();
          }}
          onClick={(e) => {
            e.stopPropagation();
            if (isIncluded) {
              if (onV2LocationReset?.(suggestion, false)) return;
              handleOnChange({
                action: 'removeTag',
                groupName: groupNameForAction,
                exclude: false,
                skipImmediateApplyOnRemove: true,
              });
              return;
            }
            if (onV2LocationPick?.(suggestion, false)) return;
            onSelectOption(groupNameForAction);
            // When a filter doesn't support include/exclude actions, selecting an
            // item should behave like a normal dropdown selection and close.
            if (hideIncludeExclude) setShowAutoComplete(false);
          }}
          onKeyDown={(e) => {
            if (e.key !== 'Enter' && e.key !== ' ') return;
            e.preventDefault();
            e.stopPropagation();
            if (isIncluded) {
              if (onV2LocationReset?.(suggestion, false)) return;
              handleOnChange({
                action: 'removeTag',
                groupName: groupNameForAction,
                exclude: false,
                skipImmediateApplyOnRemove: true,
              });
              return;
            }
            if (onV2LocationPick?.(suggestion, false)) return;
            onSelectOption(groupNameForAction);
            if (hideIncludeExclude) setShowAutoComplete(false);
          }}
          className={classNames(
            'item d-flex align-items-center position-relative justify-content-between',
            {
              'autocomplete-menu-list': isSelected,
              'autocomplete-item--applied': shouldShowAppliedState,
            },
          )}
        >
          <div className="d-flex flex-column autocomplete-item-content">
            <div className="d-flex align-items-center">
              {isCompany && suggestion?.companyLogoUrl && (
                <img
                  src={suggestion?.companyLogoUrl}
                  alt={suggestion?.companyName}
                  className="autocomplete-company-logo mr-2"
                  style={{
                    width: 20,
                    height: 20,
                    borderRadius: 2,
                    objectFit: 'contain',
                  }}
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                  }}
                />
              )}
              {isV2Response && isV2Suggestion(suggestion) && suggestion?.emoji && (
                <span className="autocomplete-emoji mr-2 d-flex align-items-center">
                  {v2FlagImageSrc ? (
                    <img
                      src={v2FlagImageSrc}
                      alt=""
                      width={22}
                      height={16}
                      className="v2-location-flag-img"
                      loading="lazy"
                      decoding="async"
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                      }}
                    />
                  ) : (
                    suggestion.emoji
                  )}
                </span>
              )}
              <TruncatedTextWithAutoTooltip
                className="autocomplete-text"
                text={suggestionValue}
              />
            </div>
            {isCompany && suggestion?.companyDomain && (
              <TruncatedTextWithAutoTooltip
                className="company-domain-truncated-text"
                text={suggestion.companyDomain}
              />
            )}
            {isV2Response && isV2Suggestion(suggestion) && suggestion?.type && (
              <p className="autocomplete-type mb-0 gray-txt-11">
                {suggestion.type}
              </p>
            )}
          </div>
          {!hideIncludeExclude &&
            renderActionArea(suggestionKey, groupNameForAction, suggestion)}
        </li>
      );
    });

  return (
    <ul
      id={scrollableId}
      role="listbox"
      className={classNames('autocomplete', {
        'autocomplete-loading': isInitialLoading,
        'with-label': inputLabel,
      })}
    >
      {isInitialLoading &&
        Array.from({ length: skeletonCount }, (_, index) => (
          <li
            key={`skeleton-loading-${filterKey}-${index}`}
            className="item d-flex align-items-center position-relative justify-content-between"
          >
            <div className="d-flex flex-column w-100">
              <SkeletonLoading width="100%" height={40} borderRadius={4} />
            </div>
          </li>
        ))}
      {!isInitialLoading && onLoadMore ? (
        <InfiniteScroll
          dataLength={suggestions?.length || 0}
          hasMore={hasMore}
          next={onLoadMore}
          loader={
            <div className="d-flex justify-content-center align-items-center py-2">
              <Loader animation="border" size="sm" className="spinner--blue" />
            </div>
          }
          endMessage={
            suggestions?.length > 0 ? (
              <div className="autocomplete-end-message d-flex justify-content-center align-items-center py-2">
                <p className="autocomplete-end-text mb-0">The End</p>
              </div>
            ) : (
              <span />
            )
          }
          scrollableTarget={scrollableId}
        >
          {renderSuggestionItems()}
        </InfiniteScroll>
      ) : (
        !isInitialLoading && renderSuggestionItems()
      )}
      {isNoResults && (
        <li className="item d-flex align-items-center justify-content-center">
          <span className="regular-2 gray-txt-11">No match found</span>
        </li>
      )}
    </ul>
  );
};

const RenderCheckboxGroupBase: React.FC<RenderCheckboxGroupProps> = ({
  label,
  name,
  checked,
  childItems,
  expanded,
  handleOnChange,
  filterKey,
  infoCircleToolTip,
  hideIncludeExclude = false,
  isIncludeDisabled,
  includeTooltip,
  isPhoneNumberNudgeOpen,
  onToggleExpand,
  tooltipText,
  parentLocked = false,
  onLockClick,
}) => {
  const isPhoneNumber = name === 'Mobile' || name === 'Phone';
  const isChecked =
    checked === true || (isPhoneNumber && isPhoneNumberNudgeOpen);
  const isExpanded = expanded?.includes(name);

  return (
    <div className="v2-checkbox-container">
      <div className="v2-checkbox">
        {childItems?.length > 0 && onToggleExpand && (
          <span className="minus-plus-icon">
            {isExpanded ? (
              <Minus onClick={() => onToggleExpand(name)} />
            ) : (
              <Plus onClick={() => onToggleExpand(name)} />
            )}
          </span>
        )}
        <Checkbox
          checked={isChecked}
          value={name}
          onChange={(check) => {
            if (parentLocked) {
              onLockClick?.();
              return;
            }
            if (!check || !isIncludeDisabled) {
              handleOnChange({ action: 'changeParent', groupName: name });
            }
          }}
          label={
            <span className="label-container">
              {tooltipText ? (
                <OverlayTooltip placement={Placement.Bottom} text={tooltipText}>
                  <span className="d-flex">
                    <TruncatedTextWithAutoTooltip
                      className="checkbox-label"
                      text={label}
                    />
                  </span>
                </OverlayTooltip>
              ) : (
                <TruncatedTextWithAutoTooltip
                  className="checkbox-label"
                  text={label}
                />
              )}
              <span className="checked-number">
                {childItems?.length > 0 && ` (${childItems?.length})`}
              </span>
              {infoCircleToolTip && (
                <OverlayTooltip
                  placement={Placement.Bottom}
                  text={infoCircleToolTip}
                >
                  <span className="ml-1 mt-1 gray-txt-11">
                    <InfoCircle width={13.33} height={13.33} />
                  </span>
                </OverlayTooltip>
              )}
              {parentLocked && hideIncludeExclude && (
                <span
                  {...(onLockClick
                    ? accessibleOnClick((e: any) => {
                        e?.stopPropagation();
                        onLockClick();
                      })
                    : {})}
                >
                  <Lock
                    width={14}
                    height={14}
                    color="#F59E0B"
                    className="accordion-lock-icon"
                  />
                </span>
              )}
            </span>
          }
          intermediate={checked === 'indeterminate'}
        />
        {!hideIncludeExclude && (
          <div className="include-exclude-icons">
            {parentLocked ? (
              <span
                {...(onLockClick
                  ? accessibleOnClick((e: any) => {
                      e?.stopPropagation();
                      onLockClick();
                    })
                  : {})}
              >
                <Lock
                  width={16}
                  height={16}
                  color="#F59E0B"
                  className="accordion-lock-icon"
                />
              </span>
            ) : (
              <>
                <OverlayTooltip
                  placement={Placement.Bottom}
                  id={`tooltip-${name}-include`}
                  className="bs-tooltip-inner"
                  text={includeTooltip}
                >
                  <CirclePlus
                    className={`circle-plus ${
                      isIncludeDisabled ? 'include-disabled' : 'include-enabled'
                    }`}
                    onClick={() => {
                      if (!isIncludeDisabled) {
                        handleOnChange({
                          action: 'includeParent',
                          groupName: name,
                        });
                      }
                    }}
                  />
                </OverlayTooltip>

                <OverlayTooltip
                  placement={Placement.Bottom}
                  id={`tooltip-${name}-exclude`}
                  className="bs-tooltip-inner"
                  text="Exclude"
                >
                  <CircleMinus
                    className="circle-minus"
                    onClick={() =>
                      handleOnChange({
                        action: 'excludeParent',
                        groupName: name,
                      })
                    }
                  />
                </OverlayTooltip>
              </>
            )}
          </div>
        )}
      </div>
      {isExpanded && (
        <div className="v2-checkbox-children">
          {childItems?.map((childItem) => (
            <div
              className="v2-checkbox"
              key={`${filterKey}-${name}-child-${childItem?.key}`}
            >
              <div
                className={classNames(' v2-checkbox-child', {
                  'locked-child-item': childItem?.isLocked,
                })}
                {...(childItem?.isLocked && onLockClick
                  ? accessibleOnClick((e: any) => {
                      e?.stopPropagation();
                      onLockClick();
                    })
                  : {})}
              >
                <Checkbox
                  key={childItem?.key}
                  value={childItem?.key}
                  disabled={!!childItem?.isLocked}
                  onChange={(check) => {
                    if (!check || !isIncludeDisabled) {
                      // prevent changing locked items
                      if (childItem?.isLocked) return;
                      handleOnChange({
                        action: 'changeChild',
                        groupName: name,
                        childName: childItem?.key,
                      });
                    }
                  }}
                  label={
                    <span className="label-container">
                      <div className="d-flex align-items-center w-100">
                        <TruncatedTextWithAutoTooltip
                          className="checkbox-label"
                          text={childItem?.value}
                        />
                        {childItem?.isLocked && (
                          <Lock
                            width={14}
                            height={14}
                            color="#F59E0B"
                            className="ml-2 accordion-lock-icon"
                          />
                        )}
                      </div>
                    </span>
                  }
                  checked={childItem?.checked}
                />
                {!hideIncludeExclude && (
                  <div className="include-exclude-icons">
                    {!childItem?.isLocked && (
                      <>
                        <OverlayTooltip
                          placement={Placement.Bottom}
                          id={`tooltip-${name}-include-child-${childItem?.key}`}
                          className="bs-tooltip-inner"
                          text={includeTooltip}
                        >
                          <CirclePlus
                            className={`circle-plus ${
                              isIncludeDisabled
                                ? 'include-disabled'
                                : 'include-enabled'
                            }`}
                            onClick={() => {
                              if (!isIncludeDisabled) {
                                handleOnChange({
                                  action: 'includeChild',
                                  groupName: name,
                                  childName: childItem?.key,
                                });
                              }
                            }}
                          />
                        </OverlayTooltip>

                        <OverlayTooltip
                          placement={Placement.Bottom}
                          id={`tooltip-${name}-exclude-child-${childItem?.key}`}
                          className="bs-tooltip-inner"
                          text="Exclude"
                        >
                          <CircleMinus
                            className="circle-minus"
                            onClick={() => {
                              handleOnChange({
                                action: 'excludeChild',
                                groupName: name,
                                childName: childItem?.key,
                              });
                            }}
                          />
                        </OverlayTooltip>
                      </>
                    )}
                    {childItem?.isLocked && (
                      <Lock
                        width={16}
                        height={16}
                        color="#F59E0B"
                        className="accordion-lock-icon"
                      />
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const childSignatureEqual = (prev: any[], next: any[]): boolean => {
  const prevLen = prev?.length ?? 0;
  const nextLen = next?.length ?? 0;
  if (prevLen !== nextLen) return false;
  for (let i = 0; i < prevLen; i += 1) {
    if (
      prev[i]?.key !== next[i]?.key ||
      prev[i]?.checked !== next[i]?.checked
    ) {
      return false;
    }
  }
  return true;
};

const areCheckboxGroupPropsEqual = (
  prev: RenderCheckboxGroupProps,
  next: RenderCheckboxGroupProps,
): boolean =>
  prev.name === next.name &&
  prev.label === next.label &&
  prev.checked === next.checked &&
  prev.filterKey === next.filterKey &&
  prev.infoCircleToolTip === next.infoCircleToolTip &&
  prev.hideIncludeExclude === next.hideIncludeExclude &&
  prev.isIncludeDisabled === next.isIncludeDisabled &&
  prev.includeTooltip === next.includeTooltip &&
  prev.isPhoneNumberNudgeOpen === next.isPhoneNumberNudgeOpen &&
  prev.tooltipText === next.tooltipText &&
  prev.parentLocked === next.parentLocked &&
  prev.handleOnChange === next.handleOnChange &&
  prev.onToggleExpand === next.onToggleExpand &&
  prev.onLockClick === next.onLockClick &&
  (prev.expanded?.includes(prev.name) ?? false) ===
    (next.expanded?.includes(next.name) ?? false) &&
  childSignatureEqual(prev.childItems, next.childItems);

export const RenderCheckboxGroup = React.memo(
  RenderCheckboxGroupBase,
  areCheckboxGroupPropsEqual,
);
